import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  deleteProperty,
  findProperty,
  updatePropertyDetails,
  setPropertyImage,
  listPropertyAppointments,
  listPropertyAppointmentFilterOptions,
} from "@/app/pmc/properties-detail/properties-detail.api.ts";
import { uuid } from "@/interfaces";
import { PropertyDetailState } from "@/app/pmc/properties-detail/properties-detail.entity.ts";
import { Property } from "@/models/properties.model.ts";
import type { PropertyContactCard } from "@/repositories/property-records";
import { appointment_status } from "@/models/appointment.model.ts";
import { ApiRequestError } from "@shyne/shared/api";

const NOT_FOUND = "not_found";

export const getPropertyDetail = createAsyncThunk("propertyDetail/get", async (id: uuid, { rejectWithValue }) => {
  try {
    return await findProperty(id);
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) return rejectWithValue(NOT_FOUND);
    throw error;
  }
});

export const deletePropertyDetail = createAsyncThunk("propertyDetail/delete", async (id: uuid) => {
  await deleteProperty(id);
  return id;
});

export const updatePropertyDetail = createAsyncThunk(
  "propertyDetail/update",
  async ({ id, updates }: { id: uuid; updates: Partial<Property> }) => {
    const { image, ...details } = updates;
    const saved = await updatePropertyDetails(id, details);
    return image instanceof File ? await setPropertyImage(id, image) : saved;
  }
);

export const fetchPropertyAppointments = createAsyncThunk(
  "propertyDetail/fetchAppointments",
  async (propertyId: uuid, { getState }) => {
    const state = (getState() as { propertyDetails: PropertyDetailState }).propertyDetails;
    const { page, pageSize } = state.appointments.pagination;
    const { status, serviceParent, assignee, search, sortKey, sortOrder } = state.appointments.filters;

    return await listPropertyAppointments(
      propertyId,
      page,
      pageSize,
      {
        status: status ? (status as appointment_status) : undefined,
        serviceParent: serviceParent || undefined,
        assignee: assignee || undefined,
        search: search || undefined,
      },
      sortKey,
      sortOrder
    );
  }
);

export const fetchPropertyAppointmentFilterOptions = createAsyncThunk(
  "propertyDetail/fetchAppointmentFilterOptions",
  async (propertyId: uuid) => await listPropertyAppointmentFilterOptions(propertyId)
);

const initialState: PropertyDetailState = {
  property: null,
  notFound: false,
  loading: false,
  deleting: false,
  error: null,
  appointments: {
    list: [],
    loading: false,
    error: null,
    total: 0,
    filters: {
      status: "",
      serviceParent: "",
      assignee: "",
      search: "",
      sortKey: "unit_number",
      sortOrder: "asc",
    },
    pagination: {
      page: 1,
      pageSize: 10,
    },
    filterOptions: {
      serviceParents: [],
      vendorNames: [],
      assigneeNames: [],
    },
  },
};

const propertyDetailSlice = createSlice({
  name: "propertyDetail",
  initialState,
  reducers: {
    setPropertyManager(state, action: PayloadAction<PropertyContactCard>) {
      if (state.property) {
        state.property.property_manager = action.payload;
      }
    },
    // Appointments filter actions
    setAppointmentStatus(state, action: PayloadAction<appointment_status | "">) {
      state.appointments.filters.status = action.payload;
      state.appointments.pagination.page = 1;
    },
    setAppointmentServiceParent(state, action: PayloadAction<string>) {
      state.appointments.filters.serviceParent = action.payload;
      state.appointments.pagination.page = 1;
    },
    setAppointmentAssignee(state, action: PayloadAction<string>) {
      state.appointments.filters.assignee = action.payload;
      state.appointments.pagination.page = 1;
    },
    setAppointmentSearch(state, action: PayloadAction<string>) {
      state.appointments.filters.search = action.payload;
      state.appointments.pagination.page = 1;
    },
    setAppointmentPage(state, action: PayloadAction<number>) {
      state.appointments.pagination.page = action.payload;
    },
    setAppointmentPageSize(state, action: PayloadAction<number>) {
      state.appointments.pagination.pageSize = action.payload;
      state.appointments.pagination.page = 1;
    },
    setAppointmentSortKey(state, action: PayloadAction<string>) {
      state.appointments.filters.sortKey = action.payload;
      state.appointments.pagination.page = 1;
    },
    setAppointmentSortOrder(state, action: PayloadAction<"asc" | "desc">) {
      state.appointments.filters.sortOrder = action.payload;
      state.appointments.pagination.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPropertyDetail.pending, (state) => {
        state.property = null;
        state.notFound = false;
        state.loading = true;
        state.error = null;
      })
      .addCase(getPropertyDetail.fulfilled, (state, action) => {
        state.property = action.payload;
        state.loading = false;
      })
      .addCase(getPropertyDetail.rejected, (state, action) => {
        state.loading = false;
        state.notFound = action.payload === NOT_FOUND;
        state.error = action.error.message ?? "Failed to load property";
      })
      .addCase(deletePropertyDetail.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deletePropertyDetail.fulfilled, (state) => {
        state.property = null;
        state.deleting = false;
      })
      .addCase(deletePropertyDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to delete property";
      })
      .addCase(updatePropertyDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePropertyDetail.fulfilled, (state, action) => {
        if (state.property) state.property.property = action.payload;

        state.loading = false;
      })
      .addCase(updatePropertyDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to update property";
      })
      // Property appointments async thunks
      .addCase(fetchPropertyAppointments.pending, (state) => {
        state.appointments.loading = true;
        state.appointments.error = null;
      })
      .addCase(fetchPropertyAppointments.fulfilled, (state, action) => {
        state.appointments.loading = false;
        state.appointments.list = action.payload.data;
        state.appointments.total = action.payload.count;
      })
      .addCase(fetchPropertyAppointments.rejected, (state, action) => {
        state.appointments.loading = false;
        state.appointments.error = action.error.message ?? "Failed to fetch appointments";
      })
      .addCase(fetchPropertyAppointmentFilterOptions.fulfilled, (state, action) => {
        state.appointments.filterOptions = action.payload;
      });
  },
});

export const {
  setPropertyManager,
  setAppointmentStatus,
  setAppointmentServiceParent,
  setAppointmentAssignee,
  setAppointmentSearch,
  setAppointmentPage,
  setAppointmentPageSize,
  setAppointmentSortKey,
  setAppointmentSortOrder,
} = propertyDetailSlice.actions;
export default propertyDetailSlice.reducer;
